import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ExternalLink } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";

interface LinkPreviewData {
  title: string | null;
  description: string | null;
  image: string | null;
  siteName: string | null;
  favicon: string | null;
  domain: string | null;
  url: string;
}

interface LinkPreviewProps {
  text: string;
  isOwn: boolean;
}

const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/gi;

// Simple in-memory cache
const previewCache = new Map<string, LinkPreviewData | null>();

export const LinkPreview = ({ text, isOwn }: LinkPreviewProps) => {
  const [preview, setPreview] = useState<LinkPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fetchedRef = useRef(false);

  const urls = text.match(URL_REGEX);
  const firstUrl = urls?.[0];

  useEffect(() => {
    if (!firstUrl || fetchedRef.current) return;
    fetchedRef.current = true;

    // Check cache first
    if (previewCache.has(firstUrl)) {
      setPreview(previewCache.get(firstUrl) || null);
      return;
    }

    const fetchPreview = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("link-preview", {
          body: { url: firstUrl },
        });

        if (error || !data?.success || !data?.data?.title) {
          previewCache.set(firstUrl, null);
          return;
        }

        previewCache.set(firstUrl, data.data);
        setPreview(data.data);
      } catch (err) {
        console.error("Error fetching link preview:", err);
        previewCache.set(firstUrl, null);
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [firstUrl]);

  if (!firstUrl || (!loading && !preview)) return null;

  const handleClick = async () => {
    if (!preview?.url) return;
    
    if (Capacitor.isNativePlatform()) {
      try {
        await Browser.open({ url: preview.url });
      } catch {
        window.open(preview.url, "_blank");
      }
    } else {
      window.open(preview.url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div className="mt-2 rounded-lg border border-border/50 bg-muted/30 p-3 animate-pulse">
        <div className="h-3 w-3/4 rounded bg-muted mb-2" />
        <div className="h-2 w-1/2 rounded bg-muted" />
      </div>
    );
  }

  if (!preview) return null;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`mt-2 w-full text-left rounded-lg border overflow-hidden transition-colors hover:bg-muted/50 ${
        isOwn ? "border-primary/20 bg-primary/5" : "border-border/50 bg-muted/30"
      }`}
    >
      {preview.image && !imageError && (
        <div className="w-full h-32 overflow-hidden">
          <img
            src={preview.image}
            alt={preview.title || ""}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        </div>
      )}
      <div className="p-2.5">
        <div className="flex items-center gap-1.5 mb-1">
          {preview.favicon && (
            <img
              src={preview.favicon}
              alt=""
              className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <span className="text-[10px] text-muted-foreground truncate uppercase tracking-wider">
            {preview.siteName || preview.domain}
          </span>
          <ExternalLink className="h-2.5 w-2.5 text-muted-foreground ml-auto flex-shrink-0" />
        </div>
        {preview.title && (
          <p className="text-xs font-medium line-clamp-2 leading-snug">
            {preview.title}
          </p>
        )}
        {preview.description && (
          <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5 leading-snug">
            {preview.description}
          </p>
        )}
      </div>
    </button>
  );
};
